import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { apiFetch, uploadImage, resolveImage } from "@/lib/api";
import { inputCls, labelCls } from "@/components/AuthShell";
import { LocationPicker } from "@/components/LocationPicker";
import { X, Upload, Loader2 } from "lucide-react";

const CONDITIONS = ["New", "Used", "Refurbished"];
const PAYMENTS = ["Cash on delivery", "Mobile Money (MTN/Airtel)", "Bank transfer"];

export function ListingForm({ initial, onSubmit, submitLabel = "Publish listing" }) {
  const [form, setForm] = useState({
    title: initial?.title || "",
    description: initial?.description || "",
    price: initial?.price ?? "",
    currency: initial?.currency || "ZMW",
    category: initial?.category || "",
    customCategory: "",
    condition: initial?.condition || "Used",
    location: initial?.location || "Lusaka",
    area: initial?.area || "",
    lat: initial?.lat ?? null,
    lng: initial?.lng ?? null,
    negotiable: initial?.negotiable ?? true,
    accepting_offers: initial?.accepting_offers ?? false,
    payment_methods: initial?.payment_methods || [],
  });
  const [images, setImages] = useState(initial?.images || []);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);

  const { data: categories = [] } = useQuery({ queryKey: ["categories"], queryFn: () => apiFetch("/categories") });
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      const urls = [];
      for (const f of files) urls.push(await uploadImage(f));
      setImages((prev) => [...prev, ...urls]);
    } catch (err) {
      toast.error(err.message || "Upload failed");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!images.length) return toast.error("Add at least one photo");
    const category = form.category === "Other" && form.customCategory.trim() ? form.customCategory.trim() : form.category;
    if (!category) return toast.error("Pick a category");
    setSaving(true);
    try {
      await onSubmit({
        title: form.title.trim(),
        description: form.description.trim(),
        price: Number(form.price),
        currency: form.currency,
        category,
        condition: form.condition,
        location: form.location.trim(),
        area: form.area.trim(),
        lat: form.lat,
        lng: form.lng,
        images,
        negotiable: form.negotiable,
        accepting_offers: form.accepting_offers,
        payment_methods: form.payment_methods,
      });
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  const togglePayment = (p) =>
    set("payment_methods", form.payment_methods.includes(p) ? form.payment_methods.filter((x) => x !== p) : [...form.payment_methods, p]);

  return (
    <form onSubmit={submit} className="space-y-6">
      {/* Photos */}
      <div>
        <label className={labelCls}>Photos</label>
        <div className="flex flex-wrap gap-3">
          {images.map((url, i) => (
            <div key={url} className="relative h-24 w-24 overflow-hidden rounded-xl border border-border">
              <img src={resolveImage(url)} alt="" className="h-full w-full object-cover" />
              <button type="button" onClick={() => setImages(images.filter((_, idx) => idx !== i))} className="absolute right-1 top-1 rounded-full bg-black/60 p-0.5 text-white" data-testid={`remove-photo-${i}`}>
                <X className="h-3.5 w-3.5" />
              </button>
            </div>
          ))}
          <label className="flex h-24 w-24 cursor-pointer flex-col items-center justify-center gap-1 rounded-xl border-2 border-dashed border-border text-muted-foreground hover:border-price hover:text-price" data-testid="add-photo">
            {uploading ? <Loader2 className="h-5 w-5 animate-spin" /> : <Upload className="h-5 w-5" />}
            <span className="text-xs">Add</span>
            <input type="file" accept="image/*" multiple hidden onChange={handleFiles} data-testid="photo-input" />
          </label>
        </div>
      </div>

      <div>
        <label className={labelCls}>Title</label>
        <input data-testid="form-title" required value={form.title} onChange={(e) => set("title", e.target.value)} className={inputCls} placeholder="e.g. iPhone 13 Pro 256GB" />
      </div>

      <div>
        <label className={labelCls}>Description</label>
        <textarea data-testid="form-description" value={form.description} onChange={(e) => set("description", e.target.value)} rows={4} className={inputCls} placeholder="Describe the item, condition, extras…" />
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <div className="sm:col-span-2">
          <label className={labelCls}>Price</label>
          <input data-testid="form-price" type="number" min="0" step="any" required value={form.price} onChange={(e) => set("price", e.target.value)} className={inputCls} />
        </div>
        <div>
          <label className={labelCls}>Currency</label>
          <select data-testid="form-currency" value={form.currency} onChange={(e) => set("currency", e.target.value)} className={inputCls}>
            <option value="ZMW">ZMW (K)</option>
            <option value="USD">USD ($)</option>
          </select>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div>
          <label className={labelCls}>Category</label>
          <select data-testid="form-category" value={form.category} onChange={(e) => set("category", e.target.value)} className={inputCls} required>
            <option value="">Select…</option>
            {categories.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
          {form.category === "Other" && (
            <input data-testid="form-custom-category" value={form.customCategory} onChange={(e) => set("customCategory", e.target.value)} className={`${inputCls} mt-2`} placeholder="Type a category" />
          )}
        </div>
        <div>
          <label className={labelCls}>Condition</label>
          <select data-testid="form-condition" value={form.condition} onChange={(e) => set("condition", e.target.value)} className={inputCls}>
            {CONDITIONS.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div>
          <label className={labelCls}>Town / City</label>
          <input data-testid="form-location" value={form.location} onChange={(e) => set("location", e.target.value)} className={inputCls} />
        </div>
        <div>
          <label className={labelCls}>Area <span className="text-muted-foreground">(optional)</span></label>
          <input data-testid="form-area" value={form.area} onChange={(e) => set("area", e.target.value)} className={inputCls} />
        </div>
      </div>

      <div>
        <label className={labelCls}>Pin location on map <span className="text-muted-foreground">(tap the map or drag the pin)</span></label>
        {form.lat != null && (
          <div className="mt-2">
            <span className="text-xs text-muted-foreground" data-testid="pin-coords">
              Pinned: {Number(form.lat).toFixed(4)}, {Number(form.lng).toFixed(4)}
            </span>
          </div>
        )}
        <div className="mt-3 max-w-xl">
          <LocationPicker lat={form.lat} lng={form.lng} onChange={(lat, lng) => setForm((f) => ({ ...f, lat, lng }))} />
        </div>
      </div>

      <div>
        <label className={labelCls}>Payment methods</label>
        <div className="flex flex-wrap gap-2">
          {PAYMENTS.map((p) => (
            <button
              type="button"
              key={p}
              onClick={() => togglePayment(p)}
              className={`rounded-full px-3.5 py-1.5 text-sm font-medium transition ${form.payment_methods.includes(p) ? "bg-brand text-white" : "bg-secondary text-foreground"}`}
            >
              {p}
            </button>
          ))}
        </div>
      </div>

      <div className="space-y-2">
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={form.negotiable} onChange={(e) => set("negotiable", e.target.checked)} data-testid="form-negotiable" className="h-4 w-4 accent-[#3F7D14]" />
          Price is negotiable
        </label>
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={form.accepting_offers} onChange={(e) => set("accepting_offers", e.target.checked)} data-testid="form-offers" className="h-4 w-4 accent-[#3F7D14]" />
          Accept offers from buyers
        </label>
      </div>

      <button data-testid="form-submit" type="submit" disabled={saving || uploading} className="w-full rounded-xl bg-price py-3 text-sm font-semibold text-white transition hover:bg-price-light disabled:opacity-60 sm:w-auto sm:px-8">
        {saving ? "Saving…" : submitLabel}
      </button>
    </form>
  );
}
